import React, { useEffect, useRef } from 'react';

/**
 * Visionneuse plein écran réutilisable — ouvre une image en grand.
 *
 * Props :
 *  - src : URL complète de l'image (ne rend rien si vide)
 *  - alt : texte alternatif
 *  - caption : légende optionnelle affichée sous l'image
 *  - onClose : appelé à la fermeture (fond, Échap, bouton)
 */
const Lightbox = ({ src, alt, caption, onClose }) => {
  const closeBtnRef = useRef(null);
  const triggerRef = useRef(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!src) return undefined;

    triggerRef.current = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeBtnRef.current?.focus();

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onCloseRef.current?.();
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      const trigger = triggerRef.current;
      if (trigger && typeof trigger.focus === 'function') {
        trigger.focus();
      }
    };
  }, [src]);

  if (!src) return null;

  return (
    <div className="lightbox-overlay" onClick={onClose} data-testid="lightbox">
      <div
        className="lightbox-content"
        role="dialog"
        aria-modal="true"
        aria-label={alt || 'Image agrandie'}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          ref={closeBtnRef}
          className="lightbox-close"
          onClick={onClose}
          aria-label="Fermer l'agrandissement"
        >
          ✕
        </button>
        <img src={src} alt={alt || ''} className="lightbox-image" />
        {caption && <p className="lightbox-caption">{caption}</p>}
      </div>
    </div>
  );
};

export default Lightbox;
